import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounceTime, delay, map, tap } from 'rxjs/operators';
import { BehaviorSubject, from, fromEvent, merge, Subject } from 'rxjs';
import { flatMap } from 'rxjs/internal/operators';
import CircularProgress from '@material-ui/core/CircularProgress';
import './App.css';
import { initializeModel } from './features/model/model-slice';
import { RootState } from './store';
import {
  extractUpdates,
  ParameterType,
  ShapeDiverValue,
  updateDataUrlCache,
  useShapeDiver,
} from './shape-diver';
import LeftPanel from './views/left-panel';
import RightPanel from './views/right-panel';
import { Export, Parameter } from 'shapediver-types';
import { useDefinitionsLoaded } from './views/hooks';
import { unsafeCast } from './utils';
import ExportContext, { ExportRequest } from './views/export-context';
import CameraControls from './views/camera-controls';
import { rememberValues } from './features/values/remembered-values';
import { getRememberedInputIds } from './config';
import { changeValues } from './features/values/values-slice';
import { endTask, startTask } from './features/progress/progress-slice';

type ExportResult = Export.ExportResult;
type ParameterDefinition = Parameter.ParameterDefinition;

function useModelValues(): Record<string, ShapeDiverValue> {
  return useSelector((root: RootState) => {
    return root.values;
  });
}

function App(): ReactElement {
  const [viewer$, containerRef] = useShapeDiver();
  const values$Ref = useRef(
    new BehaviorSubject<Record<string, ShapeDiverValue>>({})
  );

  const exports$Ref = useRef(new Subject<ExportRequest>());
  const zoomToFitClicks$Ref = useRef(new Subject<void>());

  const dispatch = useDispatch();
  const currentValues = useModelValues();
  const loaded = useDefinitionsLoaded();

  const [viewerMessage, setViewerMessage] = useState<string>('');

  useEffect(() => {
    values$Ref.current.next(currentValues);
    // console.log('Current values change');
  }, [currentValues]);

  useEffect(() => {
    const s = viewer$
      .pipe(
        flatMap((viewer) => {
          const values$ = values$Ref.current;
          const exports$ = exports$Ref.current;

          const message$ = fromEvent(
            unsafeCast(viewer.state),
            viewer.state.EVENTTYPE.MESSAGE
          ).pipe(
            map((ev) => unsafeCast<{ message: string }>(ev).message),
            debounceTime(1000),
            tap((m) => setViewerMessage(m)),
            delay(8000),
            tap(() => setViewerMessage(''))
          );

          const valuesUpdated$ = values$.pipe(
            flatMap((newValues) => {
              const changed = extractUpdates(viewer, newValues);

              if (changed.length === 0) {
                // console.log('no updates detected');
                return Promise.resolve();
              }

              // console.log('Changed: ', changed);

              const promise = viewer.parameters
                .updateAsync(changed)
                .then((result) => {
                  const definitions = viewer.parameters.get().data;
                  // console.log('Update result: ', result);
                  changed.forEach(({ id, value }) => {
                    const d = definitions.find(
                      (d) => d.id === id
                    ) as ParameterDefinition;

                    if (d.type === ParameterType.FILE) {
                      const fileContentId = d.value;
                      updateDataUrlCache(
                        id,
                        fileContentId as string,
                        String(value)
                      );
                    }
                  });
                  // console.log('------------------', result);
                  return result;
                });

              promise.catch((...args) => {
                console.error(
                  'Error updating model parameters',
                  newValues,
                  args
                );
              });

              return from(promise);
            })
          );

          const exportComplete$ = exports$.pipe(
            flatMap((data) => {
              const definitions = viewer.parameters.get().data;
              const parameters = Object.keys(data.values)
                .map((paramName) => {
                  const def = definitions.find((d) => d.name === paramName);
                  if (!def)
                    throw new Error(
                      'Parameter name not defined in model: ' + paramName
                    );
                  return { [def.id as string]: data.values[paramName] };
                })
                .reduce((acc, cur) => ({ ...acc, ...cur }), {});

              const promise = viewer.exports
                .requestAsync(
                  unsafeCast({
                    name: data.exportName,
                    parameters,
                  })
                )
                .then<ExportResult>((result) => {
                  console.log('Export complete', result.data);
                  return result.data;
                });

              promise.catch((...args) => {
                console.error('Error updating model parameters', data, args);
              });

              dispatch(startTask({ taskName: data.exportName }));

              return from(promise);
            }),
            tap((exportData) => {
              if (!exportData.content) return;
              const fileUrl = exportData.content[0].href;
              window.open(fileUrl, '_blank');
            }),
            delay(800),
            tap((exportData) => {
              const { name } = exportData;
              dispatch(endTask({ taskName: name }));
            })
          );

          const zoomComplete$ = zoomToFitClicks$Ref.current.pipe(
            flatMap(() => {
              const promise = viewer.scene.camera.zoomAsync();
              promise.catch((...args) => {
                console.error('Unable to zoom', ...args);
              });

              return from(promise);
            })
          );

          return merge(
            valuesUpdated$,
            exportComplete$,
            message$,
            zoomComplete$
          );
        })
      )
      .subscribe();

    return (): void => s.unsubscribe();
  }, [viewer$, dispatch]);

  useEffect(() => {
    const s = viewer$
      .pipe(
        tap((viewer) => {
          const parameters = viewer.parameters.get().data;
          const currentValues = values$Ref.current.getValue();
          // console.log('Parameters: ', parameters);

          const { rememberedInputs } = PW.config;
          const newValues: Record<string, ShapeDiverValue> = {};
          parameters.forEach((p) => {
            const remembered =
              rememberedInputs.indexOf(p.name as string) !== -1;
            const id = p.id as string;

            if (!remembered || currentValues[id] === undefined) {
              newValues[id] = unsafeCast(p.value);
            }
          });

          dispatch(changeValues(newValues));
          dispatch(initializeModel({ parameters }));
        }),
        flatMap((viewer) => {
          const rememberedInputsIds = getRememberedInputIds(viewer);
          return values$Ref.current.pipe(
            debounceTime(500),
            map((newValues) => {
              const filteredValues: Record<string, ShapeDiverValue> = {};
              rememberedInputsIds.forEach(
                (id) => (filteredValues[id] = newValues[id])
              );

              return filteredValues;
            }),
            tap(rememberValues)
          );
        })
      )
      .subscribe();
    // console.log('------------------');

    return () => s.unsubscribe();
  }, [viewer$, dispatch]);

  const exportContext = {
    requestExport(data: ExportRequest): void {
      exports$Ref.current.next(data);
    },
  };

  return (
    <ExportContext.Provider value={exportContext}>
      <div className="App">
        <LeftPanel />
        <div id="sdContainer" ref={containerRef} />
        <RightPanel />
        <CameraControls
          onZoomToFit={() => zoomToFitClicks$Ref.current.next()}
        />
      </div>
      {!loaded && (
        <div className="application-loader">
          <h1>POTTERWARE 4.0</h1>
          <CircularProgress color="primary" size={100} />
        </div>
      )}

      {viewerMessage && <div className="viewer-message">{viewerMessage}</div>}
    </ExportContext.Provider>
  );
}

export default App;
